import { useState } from "react";
import type { Authorization, Product } from "../../api/types";
import { t } from "../../i18n";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import {
  ALLERGEN_DISCLAIMER,
  allergenStatus,
  allergenView,
  confirmAllergens,
  suggestAllergens,
  useAllergenDictionary,
} from "./allergens";
import { AllergenChips } from "./AllergenChips";
import { productsOf } from "./menu-edit";
import type { MenuEditor } from "./useMenuEditor";

interface Props {
  editor: MenuEditor;
  authorization: Authorization;
  /** "Bekijken": open the product in the editor, at its allergens. */
  onOpen: (productId: string) => void;
}

const dayMonth = (iso: string) => {
  const date = new Date(iso);
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}`;
};

/**
 * Allergenen, as in the prototype: Tafo proposes, the shop confirms (with a disclaimer), per product,
 * a whole category, or everything still open at once (Furkan, 26/10/2026: "niet alleen per categorie");
 * "Enkel niet bevestigd" shows what is left.
 */
export function AllergensTab({ editor, authorization, onOpen }: Props) {
  // Tafo-beheer can change the allergen list: the proposals follow at once
  useAllergenDictionary();
  const { menu, toast } = editor;
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [confirming, setConfirming] = useState<Product[] | null>(null);
  const text = t.menuAdmin.allergens;
  const all = Object.values(menu.products).filter((product) => product.categoryId !== null);
  const confirmed = all.filter((product) => product.allergensConfirmed !== undefined).length;
  const openAll = all.filter((product) => product.allergensConfirmed === undefined);

  const confirm = async (list: Product[]) => {
    const at = new Date().toISOString();
    const by = authorization.staff.name;
    const products = { ...menu.products };
    for (const product of list) products[product.id] = confirmAllergens(menu, product, by, at);
    const change =
      list.length === 1
        ? `Allergenen ${list[0]!.name} bevestigd`
        : `Allergenen van ${String(list.length)} producten bevestigd`;
    if (await editor.commit({ ...menu, products }, change)) {
      toast(list.length === 1 ? text.confirmedOne(list[0]!.name) : text.confirmedMany(list.length));
    }
  };

  const sections = menu.categories
    .map((category) => {
      const products = productsOf(menu, category.id);
      const open = products.filter((product) => product.allergensConfirmed === undefined);
      const list = onlyOpen ? open : products;
      return { category, products, open, list };
    })
    .filter((section) => section.list.length > 0);

  return (
    <>
      <div className="algtop">
        <b>{text.count(confirmed, all.length)}</b>
        <label className="algchk">
          <input
            type="checkbox"
            checked={onlyOpen}
            onChange={(event) => {
              setOnlyOpen(event.target.checked);
            }}
          />{" "}
          {text.onlyOpen}
        </label>
        {openAll.length > 0 && (
          <button
            type="button"
            className="btn small primary algall"
            disabled={editor.busy}
            onClick={() => {
              setConfirming(openAll);
            }}
          >
            {text.confirmAll(openAll.length)}
          </button>
        )}
        <span className="algleg">
          <span className="alc z">{text.certain}</span>
          <span className="alc m">{text.maybe}</span>
        </span>
      </div>
      <p className="note">{text.tabNote}</p>
      {sections.length === 0 && <p className="note">{text.allDone}</p>}
      {sections.map(({ category, products, open, list }) => {
        // a proposal is only worth showing next to what is not confirmed yet, or confirmed otherwise than proposed;
        // once confirmed as proposed it says the same twice (Furkan, 18/10/2026)
        const proposals = list.some((product) => allergenStatus(menu, product) !== "ok");
        return (
          <div className="algcat" key={category.id}>
            <div className="algch">
              <b>{category.name}</b>
              <span className="note">{text.inCategory(products.length - open.length, products.length)}</span>
              <span className="spacer" />
              {open.length > 0 && (
                <button
                  type="button"
                  className="btn small"
                  disabled={editor.busy}
                  onClick={() => {
                    setConfirming(open);
                  }}
                >
                  {text.confirmCategory}
                </button>
              )}
            </div>
            <div className="algtw">
              <table className="algt">
                <thead>
                  <tr>
                    <th>{text.columns.product}</th>
                    {proposals && <th>{text.columns.proposal}</th>}
                    <th>{text.columns.confirmed}</th>
                    <th>{text.columns.status}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.map((product) => {
                    const suggestion = suggestAllergens(menu, product);
                    const view = allergenView(menu, product);
                    const status = allergenStatus(menu, product);
                    return (
                      <tr key={product.id}>
                        <td>
                          <button
                            type="button"
                            className="linkbtn"
                            onClick={() => {
                              onOpen(product.id);
                            }}
                          >
                            {product.name}
                          </button>
                        </td>
                        {proposals && (
                          <td className="algprop" data-label={status !== "ok" ? text.columns.proposal : undefined}>
                            {status !== "ok" && (
                              <div className="alcs">
                                <AllergenChips
                                  certain={suggestion.certain}
                                  maybe={suggestion.maybe}
                                  suggestion={suggestion}
                                />
                              </div>
                            )}
                          </td>
                        )}
                        <td
                          className={view.own ? "algconf" : "algconf none"}
                          data-label={proposals ? text.columns.confirmed : undefined}
                        >
                          <div className="alcs">
                            {view.own ? (
                              <>
                                <AllergenChips certain={new Set([...view.certain, ...view.maybe])} maybe={new Set()} />
                                {!view.confirmed && <small className="note">{text.notYet}</small>}
                              </>
                            ) : (
                              <span className="note">—</span>
                            )}
                          </div>
                        </td>
                        <td className="algstat">
                          {status === "ok" && view.confirmed && (
                            <span className="pill ok">{text.okOn(dayMonth(view.confirmed.at))}</span>
                          )}
                          {status === "diff" && view.confirmed && (
                            <span
                              className="pill warn"
                              title={text.diffTitle(dayMonth(view.confirmed.at), view.confirmed.by)}
                            >
                              {text.diff}
                            </span>
                          )}
                          {status === "open" && <span className="pill">{text.open}</span>}
                        </td>
                        <td className="r">
                          {status === "open" && (
                            <button
                              type="button"
                              className="btn small"
                              disabled={editor.busy}
                              onClick={() => {
                                setConfirming([product]);
                              }}
                            >
                              {text.confirm}
                            </button>
                          )}
                          {status === "diff" && (
                            <button
                              type="button"
                              className="btn small"
                              onClick={() => {
                                onOpen(product.id);
                              }}
                            >
                              {text.view}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      {confirming && (
        <ConfirmDialog
          message={ALLERGEN_DISCLAIMER}
          confirm={text.confirm}
          onConfirm={() => void confirm(confirming)}
          onClose={() => {
            setConfirming(null);
          }}
        />
      )}
    </>
  );
}
